import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, ButtonContainer, Chip, FormControlLabel, Icon, ScrollableDrawer, Skeleton, Spacer, Stack, TextField, Typography, useCurrentBreakpoint, useTheme } from '@emburse/embark-core';
import { SyncIcon } from '@emburse/embark-icons';
import { userTimezoneString } from '../../../util/environment';
import { isValidDate, formatDate, dateToISOString, dayJsFormatDate } from '../../../util/dateAdapter';
import { getDataInterchanges, saveDataInterchange } from '../../../api/dataInterchange';
import { createDataInterchangeConfig } from '../../../api/dataInterchangeconfig';
import { DataInterchangeConfig } from '../../../interfaces/DataInterchangeConfig';
import useAccountConfig from '../../../hooks/useAccountConfig';
import { $t, useTranslation } from '../../../hooks/useTranslation';
import useLoadingOverlay from '../../../hooks/useLoadingOverlay';
function ERPGatewayScheduleDrawer({ open, isActiveStatus, isSyncPending, openUpdated, onCancel, onSave, catchError, onImportSync, onExportSync }) {
    useTranslation();
    const currentBreakpoint = useCurrentBreakpoint();
    const theme = useTheme();
    const { accountConfig } = useAccountConfig();
    const { applyLoadingState, removeLoadingState } = useLoadingOverlay();
    const [importDataInterchange, setImportDataInterchange] = useState({});
    const [exportDataInterchange, setExportDataInterchange] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const getDIs = useCallback(async () => {
        try {
            await getDataInterchanges('NetSuite').then((resp) => {
                const data = JSON.parse(resp.Data);
                if (!data.length) {
                    setImportDataInterchange({});
                    setExportDataInterchange({});
                    return;
                }
                data.forEach((di) => {
                    if (di.Name.includes('NetSuiteImport')) {
                        setImportDataInterchange((prevState) => ({ ...prevState, ...di }));
                    }
                    if (di.Name.includes('NetSuiteExport')) {
                        setExportDataInterchange((prevState) => ({ ...prevState, ...di }));
                    }
                });
            });
        }
        catch (err) {
            catchError(err);
        }
    }, [catchError]);
    useEffect(() => {
        if (open) {
            getDIs();
        }
        else {
            setImportDataInterchange({});
            setExportDataInterchange({});
        }
    }, [open, getDIs]);
    const newDIConfig = (type) => {
        const config = new DataInterchangeConfig({});
        config.Name = `${accountConfig?.Emp?.Company?.Name} - NetSuite${type}`;
        const actions = [
            {
                Attributes: {
                    type: 'SystemIntegration',
                    IntegrationName: 'NetSuite',
                    IntegrationType: type
                }
            }
        ];
        config.Actions = actions;
        return config;
    };
    const days = [
        { field: 'Sched_Mon', label: $t('Monday') },
        { field: 'Sched_Tue', label: $t('Tuesday') },
        { field: 'Sched_Wed', label: $t('Wednesday') },
        { field: 'Sched_Thu', label: $t('Thursday') },
        { field: 'Sched_Fri', label: $t('Friday') },
        { field: 'Sched_Sat', label: $t('Saturday') },
        { field: 'Sched_Sun', label: $t('Sunday') }
    ];
    const getScheduledStartTime = (type) => {
        const dateType = type === 'import'
            ? importDataInterchange.Sched_StartTime
            : exportDataInterchange.Sched_StartTime;
        const dat = new Date(dateType); // use data model date here
        if (isValidDate(dat)) {
            const isoDate = dateToISOString(dat);
            const today = dayJsFormatDate(new Date(), 'YYYY-MM-DD');
            const concatDate = `${today}${isoDate.slice(10)}`;
            const finalDate = new Date(concatDate);
            return formatDate(finalDate, 'fullTime24h');
        }
        return '';
    };
    const schedStartTimeChangeHandler = (e, type) => {
        const [hours, minutes, seconds] = e.target.value.split(':');
        const date = new Date();
        date.setHours(parseInt(hours, 10) || 0);
        date.setMinutes(parseInt(minutes, 10) || 0);
        date.setSeconds(parseInt(seconds, 10) || 0);
        if (type === 'import') {
            setImportDataInterchange((prevState) => ({ ...prevState, Sched_StartTime: date }));
        }
        else {
            setExportDataInterchange((prevState) => ({ ...prevState, Sched_StartTime: date }));
        }
    };
    const defaultTime = () => {
        const isoDate = dateToISOString(new Date());
        const today = dayJsFormatDate(new Date(), 'YYYY-MM-DD');
        const concatDate = `${today}${isoDate.slice(10)}`;
        const finalDate = new Date(concatDate);
        const simpleDate = formatDate(finalDate, 'fullTime24h');
        const [hours, minutes, seconds] = simpleDate.split(':');
        const date = new Date();
        date.setHours(parseInt(hours, 10) || 0);
        date.setMinutes(parseInt(minutes, 10) || 0);
        date.setSeconds(parseInt(seconds, 10) || 0);
        return date;
    };
    const chipChangeHandler = (field, type) => {
        if (type === 'import') {
            setImportDataInterchange((prevState) => ({ ...prevState, [field]: !prevState[field] }));
            if (!importDataInterchange?.Sched_StartTime) {
                setImportDataInterchange((prevState) => ({
                    ...prevState,
                    Sched_StartTime: defaultTime()
                }));
            }
        }
        else {
            setExportDataInterchange((prevState) => ({ ...prevState, [field]: !prevState[field] }));
            if (!exportDataInterchange?.Sched_StartTime) {
                setExportDataInterchange((prevState) => ({
                    ...prevState,
                    Sched_StartTime: defaultTime()
                }));
            }
        }
    };
    const saveScheduleHandler = async () => {
        applyLoadingState(30000);
        setIsSaving(true);
        try {
            // check if import /export data interchange exists
            // if not create new for each
            // then createDataInterchangeConfig() for each if need be
            if (!importDataInterchange?.Type) {
                const newImportConfig = newDIConfig('Import');
                await createDataInterchangeConfig(newImportConfig);
            }
            if (!exportDataInterchange?.Type) {
                const newExportConfig = newDIConfig('Export');
                await createDataInterchangeConfig(newExportConfig);
            }
            // then getDataInterchanges() for, each merge with current set up
            await getDIs();
            // then saveDataInterchange() for each
            if (importDataInterchange?.Sched_StartTime) {
                await saveDataInterchange(importDataInterchange);
            }
            if (exportDataInterchange?.Sched_StartTime) {
                await saveDataInterchange(exportDataInterchange);
            }
            // then getDIs() again
            await getDIs();
            onSave();
            setIsSaving(false);
        }
        catch (err) {
            catchError(err);
        }
        removeLoadingState();
    };
    const timeDisabled = (type) => {
        if (type === 'import') {
            return (!importDataInterchange.Sched_Mon &&
                !importDataInterchange.Sched_Tue &&
                !importDataInterchange.Sched_Wed &&
                !importDataInterchange.Sched_Thu &&
                !importDataInterchange.Sched_Fri &&
                !importDataInterchange.Sched_Sat &&
                !importDataInterchange.Sched_Sun);
        }
        return (!exportDataInterchange.Sched_Mon &&
            !exportDataInterchange.Sched_Tue &&
            !exportDataInterchange.Sched_Wed &&
            !exportDataInterchange.Sched_Thu &&
            !exportDataInterchange.Sched_Fri &&
            !exportDataInterchange.Sched_Sat &&
            !exportDataInterchange.Sched_Sun);
    };
    return (_jsx(ScrollableDrawer, { anchor: "right", open: open, sideWidth: currentBreakpoint.current === 'xs' ? 380 : 450, disableCloseOnBackdropClick: true, openUpdated: openUpdated, header: $t('Schedule'), footer: _jsxs(ButtonContainer, { alignButtons: "right", children: [_jsx(Button, { variant: "text", onClick: onCancel, children: $t('Cancel') }), _jsx(Button, { onClick: saveScheduleHandler, disabled: !isActiveStatus || isSyncPending, children: $t('Save') })] }), children: _jsxs(Stack, { spacing: 2, direction: "column", alignItems: "stretch", justifyContent: "space-between", children: [!isActiveStatus && (_jsx(Alert, { severity: "error", children: $t('The NetSuite sync is currently inactive. Verify your configuration on the Settings page to reactivate they sync.') })), _jsxs(Typography, { variant: "body2", children: [$t('Set a schedule to automate your data import and export. If you prefer to synchronize data manually, select '), _jsx("strong", { children: $t('Import Now') }), $t(' or '), _jsx("strong", { children: $t('Export Now') }), $t(' to immediately begin a sync.')] }), _jsx(Spacer, { size: 30 }), _jsxs(Stack, { spacing: 2, direction: "row", justifyContent: "space-between", children: [_jsx(Typography, { variant: "h5", children: $t('Import') }), _jsx(Button, { variant: "text", size: "small", startIcon: _jsx(Icon, { iconUrl: SyncIcon }), disabled: !isActiveStatus || isSyncPending, onClick: onImportSync, children: $t('Import Now') })] }), _jsx(Typography, { children: $t('Day') }), !isSaving && (_jsx("div", { style: { display: 'flex', flexWrap: 'wrap', gap: 8 }, children: days.map(({ field, label }) => (_jsx(Chip, { label: label, color: "primary", variant: "outlined", size: "mini", disabled: isSyncPending, style: {
                            backgroundColor: importDataInterchange[field]
                                ? theme.palette.primary.main
                                : 'initial',
                            color: importDataInterchange[field]
                                ? theme.palette.primary.contrastText
                                : theme.palette.primary.main
                        }, onClick: () => chipChangeHandler(field, 'import') }, field))) })), isSaving && (_jsxs(_Fragment, { children: [_jsx(Skeleton, { variant: "rect", height: 20 }), _jsx(Skeleton, { variant: "rect", height: 20, style: { marginTop: '8px', width: '135px' } })] })), _jsx(Typography, { children: $t('Time') }), !isSaving && (_jsx(FormControlLabel, { control: _jsx(TextField, { type: "time", value: getScheduledStartTime('import'), onChange: (e) => schedStartTimeChangeHandler(e, 'import'), style: { marginRight: '8px' } }), label: userTimezoneString, labelPlacement: "end", disabled: timeDisabled('import') || isSyncPending })), isSaving && (_jsx(Skeleton, { variant: "rect", height: 40, width: 160, style: { marginTop: '16px' } })), _jsx(Spacer, { size: 30 }), _jsxs(Stack, { spacing: 2, direction: "row", justifyContent: "space-between", children: [_jsx(Typography, { variant: "h5", children: $t('Export') }), _jsx(Button, { variant: "text", size: "small", startIcon: _jsx(Icon, { iconUrl: SyncIcon }), disabled: !isActiveStatus || isSyncPending, onClick: onExportSync, children: $t('Export Now') })] }), _jsx(Typography, { children: $t('Day') }), !isSaving && (_jsx("div", { style: { display: 'flex', flexWrap: 'wrap', gap: 8 }, children: days.map(({ field, label }) => (_jsx(Chip, { label: label, color: "primary", variant: "outlined", size: "mini", disabled: isSyncPending, style: {
                            backgroundColor: exportDataInterchange[field]
                                ? theme.palette.primary.main
                                : 'initial',
                            color: exportDataInterchange[field]
                                ? theme.palette.primary.contrastText
                                : theme.palette.primary.main
                        }, onClick: () => chipChangeHandler(field, 'export') }, field))) })), isSaving && (_jsxs(_Fragment, { children: [_jsx(Skeleton, { variant: "rect", height: 20 }), _jsx(Skeleton, { variant: "rect", height: 20, style: { marginTop: '8px', width: '135px' } })] })), _jsx(Typography, { children: $t('Time') }), !isSaving && (_jsx(FormControlLabel, { control: _jsx(TextField, { type: "time", value: getScheduledStartTime('export'), onChange: (e) => schedStartTimeChangeHandler(e, 'export'), style: { marginRight: '8px' } }), label: userTimezoneString, labelPlacement: "end", disabled: timeDisabled('export') || isSyncPending })), isSaving && (_jsx(Skeleton, { variant: "rect", height: 40, width: 160, style: { marginTop: '16px' } }))] }) }));
}
export default ERPGatewayScheduleDrawer;
